import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./auth";
import { api } from "./api";
import { toast } from "sonner";

const CartCtx = createContext(null);
const KEY_PREFIX = "pg_cart_v3_";

// One-time cleanup of legacy anonymous cart key
try { localStorage.removeItem("pg_cart_v1"); } catch { /* ignore */ }

const REASON_MSG = {
  deleted: "لم يعد المنتج متوفراً",
  agency_inactive: "الوكالة لم تعد نشطة",
  unavailable: "المنتج غير متاح للطلب",
  variant_removed: "الصنف لم يعد متوفراً",
  out_of_stock: "نفدت الكمية",
};

export function CartProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id || null;
  const key = userId ? `${KEY_PREFIX}${userId}` : null;

  const [items, setItems] = useState([]);
  const lastValidatedRef = useRef(null);

  // Load per-user cart when auth changes
  useEffect(() => {
    if (!key) { setItems([]); return; }
    try { setItems(JSON.parse(localStorage.getItem(key) || "[]")); } catch { setItems([]); }
  }, [key]);

  // Persist under the current user's key
  useEffect(() => {
    if (!key) return;
    localStorage.setItem(key, JSON.stringify(items));
  }, [items, key]);

  // Auto-prune: after cart loads for a user, validate items against the server.
  // Runs once per (user, cart-fingerprint) — no polling.
  const validate = useCallback(async (currentItems) => {
    if (!currentItems.length) return;
    const fingerprint = currentItems.map((x) => x.key).sort().join("|");
    if (lastValidatedRef.current === fingerprint) return;
    lastValidatedRef.current = fingerprint;
    try {
      const r = await api.post("/cart/validate", {
        items: currentItems.map((x) => ({
          product_id: x.product_id, variant_id: x.variant_id, quantity: x.quantity,
        })),
      });
      const validMap = new Map((r.data.valid || []).map((v) => [`${v.product_id}::${v.variant_id || ""}`, v]));
      setItems((cur) => {
        const next = [];
        for (const it of cur) {
          const v = validMap.get(it.key);
          if (!v) continue; // removed
          next.push({
            ...it,
            name: v.name,
            variant_name: v.variant_name,
            unit_price: v.unit_price,
            cover_image: v.cover_image || it.cover_image,
            max_stock: v.max_stock,
            quantity: Math.min(it.quantity, v.max_stock || 99),
          });
        }
        // Reset fingerprint if pruning changed the cart so subsequent adds trigger revalidation
        if (next.length !== cur.length) {
          const removed = r.data.removed || [];
          const first = removed[0];
          const msg = removed.length === 1
            ? `تم تحديث السلة: ${REASON_MSG[first?.reason] || "منتج لم يعد متوفراً"}`
            : `تم تحديث السلة: أُزيلت ${removed.length} منتجات لم تعد متوفرة`;
          toast.info(msg);
        }
        return next;
      });
    } catch { /* silent — cart falls back to stale data, checkout will re-validate */ }
  }, []);

  useEffect(() => {
    if (!userId) return;
    validate(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, items.length]);

  const add = (product, variant, qty = 1) => {
    if (!userId) return false;
    setItems((cur) => {
      const k = `${product.id}::${variant?.id || ""}`;
      const idx = cur.findIndex((x) => x.key === k);
      if (idx >= 0) {
        const next = [...cur];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...cur, {
        key: k,
        product_id: product.id,
        variant_id: variant?.id || null,
        name: product.name,
        variant_name: variant?.name || null,
        unit_price: variant ? variant.price : product.final_price,
        cover_image: product.cover_image,
        quantity: qty,
        max_stock: variant?.stock ?? 99,
      }];
    });
    lastValidatedRef.current = null; // re-validate on next tick
    return true;
  };
  const remove = (k) => setItems((c) => c.filter((x) => x.key !== k));
  const setQty = (k, q) => setItems((c) => c.map((x) => x.key === k ? { ...x, quantity: Math.max(1, Math.min(q, x.max_stock || 99)) } : x));
  const clear = () => setItems([]);
  const count = items.reduce((a, b) => a + b.quantity, 0);
  const total = items.reduce((a, b) => a + b.quantity * b.unit_price, 0);

  return <CartCtx.Provider value={{ items, add, remove, setQty, clear, count, total }}>{children}</CartCtx.Provider>;
}

export const useCart = () => useContext(CartCtx);
